// ---------------- EmailContestar.jsx -----------------

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Typography,
  CircularProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import DropZoneWrapper from "../dropwrapper/DropZoneWrapper";
import TipTapEditorContestar from "./TipTapEditorContestar";
import axios from "axios";
import { validateRecipientField } from "../emailRecipients";
import {
  getReplyFromEmail,
  getReplyToAddress,
} from "../whatsappEmail/threadGrouping";
import { emailMessagePath } from "../emailApiPaths";
import { looksLikeTruncatedQuotedBody } from "../truncatedBodyHeuristic";

const API = process.env.REACT_APP_API_URL;

const ACCENT = "#00bbcd";
const ACCENT_HOVER = "#00a5b5";
const ON_ACCENT = "#0f172a";

const accentBtnSx = {
  bgcolor: ACCENT,
  color: ON_ACCENT,
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
};

const accentOutlinedSx = {
  color: ACCENT,
  borderColor: ACCENT,
  "&:hover": {
    borderColor: ACCENT_HOVER,
    bgcolor: "rgba(0, 187, 205, 0.08)",
  },
};

const accentTextSx = {
  color: ACCENT,
  "&:hover": { bgcolor: "rgba(0, 187, 205, 0.08)" },
};

export default function EmailContestar({
  open,
  onClose,
  onSend,
  email,
  accounts,
  originalHtml,
}) {
  const editorRef = useRef(null);
  const quotedHtmlRef = useRef("");

  const [fromEmail, setFromEmail] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");

  const [selectedSignature, setSelectedSignature] = useState("main");
  const [signatures, setSignatures] = useState({ main: "", reply: "" });

  const [attachments, setAttachments] = useState([]);
  const [initialHtml, setInitialHtml] = useState("");
  const [quotedHtml, setQuotedHtml] = useState("");
  const [resolvedOriginalHtml, setResolvedOriginalHtml] = useState("");
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const buildQuotedBlock = (html) => {
    if (!html || !email) return "";
    return `
      <br/><br/>
      ---------- Mensaje original ----------
      <br/><b>De:</b> ${email.from}<br/>
      <b>Fecha:</b> ${new Date(email.date).toLocaleString("es-ES")}<br/>
      <b>Asunto:</b> ${email.subject}<br/><br/>
      ${html}
    `;
  };

  const closeDialogSafely = () => {
    onClose?.();
  };

  /* -----------------------------------------------------
        RESET AL ABRIR
  ----------------------------------------------------- */
  useEffect(() => {
    if (!open || !email) return;

    setAttachments([]);

    const replyTo = getReplyToAddress(email, accounts);
    setTo(replyTo || email.from || "");
    setSubject(`Re: ${email.subject || ""}`.replace(/^Re:\s*Re:\s*/i, "Re: "));

    const replyFrom = getReplyFromEmail(email, accounts);
    if (replyFrom) {
      setFromEmail(replyFrom);
    } else if (accounts?.length > 0) {
      setFromEmail(accounts[0].email.toLowerCase());
    }
  }, [open, email, accounts]);

  /* -----------------------------------------------------
        CARGAR FIRMAS
  ----------------------------------------------------- */
  useEffect(() => {
    if (!open) return;
    if (!fromEmail) return;

    axios.get(`${API}/signatures/${fromEmail}`).then((res) => {
      const main = res.data.signature_html || "";
      const reply = res.data.reply_signature_html || main;
      setSignatures({ main, reply });
    });
  }, [open, fromEmail]);

  /* Cuerpo original: caché del padre o fetch si falta */
  useEffect(() => {
    if (!open || !email?.gmailId) {
      setResolvedOriginalHtml("");
      setLoadingOriginal(false);
      return;
    }

    const cached = originalHtml || "";
    const cachedOk =
      cached &&
      !cached.includes("Vista previa del índice") &&
      !looksLikeTruncatedQuotedBody(cached);

    if (cachedOk) {
      setResolvedOriginalHtml(cached);
      setLoadingOriginal(false);
      return;
    }

    let cancelled = false;
    setLoadingOriginal(true);

    (async () => {
      try {
        let html = "";
        for (let attempt = 0; attempt < 3; attempt++) {
          const url =
            attempt > 0
              ? `${emailMessagePath(API, email.gmailId, "body")}?_ts=${Date.now()}`
              : emailMessagePath(API, email.gmailId, "body");
          const res = await axios.get(url, { timeout: 45_000 });
          html = res.data.html || res.data.text || "";
          const usable =
            html &&
            !res.data.partial &&
            !looksLikeTruncatedQuotedBody(html);
          if (usable) break;
          if (attempt + 1 < 3) {
            await new Promise((r) => setTimeout(r, 2000 + attempt * 1500));
          }
        }
        if (!cancelled) {
          const usable = html && !looksLikeTruncatedQuotedBody(html);
          setResolvedOriginalHtml(usable ? html : "");
        }
      } catch (err) {
        console.error("Error cargando cuerpo para responder:", err);
        if (!cancelled) setResolvedOriginalHtml("");
      } finally {
        if (!cancelled) setLoadingOriginal(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, email, originalHtml]);

  /* Bloque citado intacto (fuera de TipTap) */
  useEffect(() => {
    if (!open || !resolvedOriginalHtml || !email) {
      setQuotedHtml("");
      quotedHtmlRef.current = "";
      return;
    }
    const block = buildQuotedBlock(resolvedOriginalHtml);
    quotedHtmlRef.current = block;
    setQuotedHtml(block);
  }, [open, resolvedOriginalHtml, email]);

  /* Editor: respuesta editable + firma; el mensaje original no pasa por TipTap */
  useEffect(() => {
    if (!open) return;

    const signatureHTML =
      selectedSignature === "main"
        ? signatures.main
        : selectedSignature === "reply"
        ? signatures.reply
        : "";

    const content = `<p></p>${signatureHTML}`;

    if (!editorRef.current) {
      setInitialHtml(content);
    } else {
      editorRef.current.commands.setContent(content, false);
    }
  }, [open, signatures, selectedSignature]);

  /* -----------------------------------------------------
        MANEJO DE NUEVOS ARCHIVOS
  ----------------------------------------------------- */
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (name) => {
    setAttachments((prev) => prev.filter((f) => f.name !== name));
  };

  /* -----------------------------------------------------
        ENVIAR RESPUESTA
  ----------------------------------------------------- */
  const sendReply = async () => {
    if (isSending) return;
    if (!fromEmail) {
      alert("Selecciona la cuenta desde la que responder.");
      return;
    }
    if (!to.trim()) {
      alert("Debe indicar un destinatario.");
      return;
    }

    const recipients = validateRecipientField(to);
    if (!recipients.ok) {
      alert(`❌ ${recipients.message}`);
      return;
    }

    const userHtml = editorRef.current?.getHTML?.() || "";
    const html = `${userHtml}${quotedHtmlRef.current || ""}`;

    const form = new FormData();
    form.append("fromEmail", fromEmail);
    form.append("to", recipients.normalized);
    form.append("subject", subject);
    form.append("html", html);
    form.append("isReply", "true");
    form.append("skipServerSignature", "true");

    attachments.forEach((file) => form.append("attachments", file));

    setIsSending(true);
    try {
      await onSend(form);
      closeDialogSafely();
    } catch (err) {
      console.error("Error enviando respuesta:", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Error enviando la respuesta";
      alert(`❌ ${msg}`);
    } finally {
      setIsSending(false);
    }
  };

  /* -----------------------------------------------------
        UI
  ----------------------------------------------------- */
  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={isSending ? undefined : onClose}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Responder mensaje
        <IconButton onClick={onClose} disabled={isSending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* FROM */}
        <Select
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          value={fromEmail}
          onChange={(e) => setFromEmail(e.target.value)}
        >
          {accounts.map((acc) => (
            <MenuItem key={acc.id} value={acc.email.toLowerCase()}>
              {acc.email}
            </MenuItem>
          ))}
        </Select>

        {/* PARA */}
        <TextField
          fullWidth
          size="small"
          label="Para"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* ASUNTO */}
        <TextField
          fullWidth
          size="small"
          label="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* SELECT FIRMA */}
        <Select
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          value={selectedSignature}
          onChange={(e) => setSelectedSignature(e.target.value)}
        >
          <MenuItem value="reply">Firma de respuesta</MenuItem>
          <MenuItem value="main">Firma principal</MenuItem>
          <MenuItem value="none">Sin firma</MenuItem>
        </Select>

        {/* BOTÓN ADJUNTAR */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AttachFileIcon />}
          component="label"
          sx={{ mb: 1, ...accentOutlinedSx }}
        >
          Adjuntar archivos
          <input type="file" hidden multiple onChange={handleFileChange} />
        </Button>

        {/* Adjuntos nuevos */}
        {attachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {attachments.map((file) => (
              <Chip
                key={file.name}
                label={file.name}
                onDelete={() => removeAttachment(file.name)}
              />
            ))}
          </Box>
        )}

        {/* Respuesta editable (firma + texto nuevo) */}
        <DropZoneWrapper
          onFiles={(files) => setAttachments((prev) => [...prev, ...files])}
        >
          <TipTapEditorContestar
            editorRef={editorRef}
            initialHtml={initialHtml}
            onChange={() => {}}
          />
        </DropZoneWrapper>

        {/* Mensaje original citado intacto */}
        {quotedHtml ? (
          <Box
            sx={{
              mt: 2,
              border: "1px solid #e2e8f0",
              borderRadius: 1,
              p: 2,
              bgcolor: "#fafafa",
              maxHeight: 420,
              overflow: "auto",
              fontSize: 14,
              "& table": { borderCollapse: "collapse", width: "100%" },
              "& img": { maxWidth: "100%", height: "auto" },
            }}
            dangerouslySetInnerHTML={{ __html: quotedHtml }}
          />
        ) : loadingOriginal ? (
          <Typography sx={{ color: "#777", mt: 2 }}>
            Cargando mensaje original…
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSending} sx={accentTextSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={sendReply}
          disabled={isSending}
          startIcon={
            isSending ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={accentBtnSx}
        >
          {isSending ? "Enviando..." : "Enviar respuesta"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
